/**
 * useUnseenCounts — small hook to fetch and cache the "new chapters since
 * last visit" map (series_id → count) for the current user.
 *
 * Usage:
 *   const { counts, markSeen } = useUnseenCounts();
 *   const newCount = counts[series.series_id] || 0;
 *   onClick={() => { markSeen(series.series_id); ... }}
 */
import { useCallback, useEffect, useState } from 'react';
import { authFetch } from './authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useUnseenCounts = () => {
  const [counts, setCounts] = useState({});

  const refetch = useCallback(async () => {
    try {
      const res = await authFetch(`${BACKEND_URL}/api/library/unseen-counts`);
      if (res.ok) setCounts(await res.json());
    } catch {/* noop */}
  }, []);

  useEffect(() => { refetch(); }, [refetch]);

  const markSeen = useCallback(async (seriesId) => {
    if (!seriesId) return;
    // Optimistic clear of the badge.
    setCounts((c) => {
      if (!c[seriesId]) return c;
      const { [seriesId]: _gone, ...rest } = c;
      return rest;
    });
    try {
      await authFetch(`${BACKEND_URL}/api/library/series/${seriesId}/seen`, {
        method: 'POST',
      });
    } catch {/* noop — optimistic state already reflects the click */}
  }, []);

  /**
   * Catch-up: clear NEW badges across series in one shot.
   * If `seriesIds` is omitted, clears all unseen badges.
   * If provided, clears only the badges for that subset (used by Vault).
   * Returns `{ marked, bonus_coins }`.
   */
  const markAllSeen = useCallback(async (seriesIds) => {
    const previous = counts;
    let toClear;
    if (Array.isArray(seriesIds)) {
      const scope = new Set(seriesIds);
      toClear = Object.keys(previous).filter((sid) => scope.has(sid));
    } else {
      toClear = Object.keys(previous);
    }
    const marked = toClear.length;
    if (marked === 0) return { marked: 0, bonus_coins: 0 };
    // Optimistic local clear of only the targeted ids.
    setCounts((c) => {
      const next = { ...c };
      for (const sid of toClear) delete next[sid];
      return next;
    });
    let bonus = 0;
    try {
      const res = await authFetch(`${BACKEND_URL}/api/library/series/seen-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: Array.isArray(seriesIds)
          ? JSON.stringify({ series_ids: seriesIds })
          : JSON.stringify({}),
      });
      if (res.ok) {
        const data = await res.json();
        bonus = data?.bonus_coins || 0;
      }
    } catch {/* noop — optimistic state already reflects the click */}
    return { marked, bonus_coins: bonus };
  }, [counts]);

  return { counts, refetch, markSeen, markAllSeen };
};

export default useUnseenCounts;
